import { useState, useEffect } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import DropDown from '../dropdown/DropDown';
import DatePicker from '../datepicker/DatePicker';
import P from '../texts/p/P';
import Spinner from '../Spinner/spinner';
import { toast } from 'react-toastify';
import { 
  createProtectorShareReceipt, 
  getProtectorShareReceiptFormMetadata 
} from '../../services/GuaranteesService';

interface FormMetadata {
  partners: Array<{
    id: number;
    name: string;
    cuit?: string | null;
    share_quantity?: number | null;
    ceded_share_quantity?: number | null;
    available_share_quantity?: number | null;
  }>;
  protectors: Array<{ id: number; name: string }>;
  statuses: Array<{ id: number; name: string }>;
}

interface FormValues {
  partner_id: number | null;
  protector_id: number | null;
  minute_date: string;
  minute_number: string;
  quantity: number | null;
}

interface NewProtectorShareReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  protectorId?: string;
}

const NewProtectorShareReceiptModal = ({
  isOpen,
  onClose,
  onSuccess,
  protectorId,
}: NewProtectorShareReceiptModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [metadata, setMetadata] = useState<FormMetadata | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({
    partner_id: null,
    protector_id: protectorId ? parseInt(protectorId) : null,
    minute_date: new Date().toISOString().split('T')[0],
    minute_number: '',
    quantity: null,
  });

  useEffect(() => {
    if (isOpen) {
      loadFormMetadata();
    }
  }, [isOpen]);

  useEffect(() => {
    if (protectorId && metadata) {
      const protectorIdNum = parseInt(protectorId);
      const protectorExists = metadata.protectors.some(p => p.id === protectorIdNum);
      if (protectorExists) {
        setFormValues(prev => ({ ...prev, protector_id: protectorIdNum }));
      }
    }
  }, [protectorId, metadata]);

  const loadFormMetadata = async () => {
    setIsLoadingMetadata(true);
    try {
      const response = await getProtectorShareReceiptFormMetadata(protectorId);
      if (response.success && 'data' in response) {
        setMetadata(response.data);
        // Set protector_id if provided
        if (protectorId) {
          const protectorIdNum = parseInt(protectorId);
          const protectorExists = response.data.protectors.some((p: { id: number }) => p.id === protectorIdNum);
          if (protectorExists) {
            setFormValues(prev => ({ ...prev, protector_id: protectorIdNum }));
          }
        }
      } else {
        toast.error('Error al cargar los metadatos del formulario');
      }
    } catch (error) {
      console.error('Error loading form metadata:', error);
      toast.error('Error al cargar los metadatos del formulario');
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      partner_id: null,
      protector_id: protectorId ? parseInt(protectorId) : null,
      minute_date: new Date().toISOString().split('T')[0],
      minute_number: '',
      quantity: null,
    });
    setSubmitError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const updateFormValues = (updates: Partial<FormValues>) => {
    setFormValues((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formValues.partner_id || !formValues.protector_id || !formValues.minute_date || !formValues.minute_number || !formValues.quantity) {
      setSubmitError('Por favor complete todos los campos requeridos');
      return;
    }

    const selectedPartner = metadata?.partners?.find((p) => p.id === formValues.partner_id);
    const available = selectedPartner?.available_share_quantity ?? null;
    if (available != null && formValues.quantity > available) {
      setSubmitError(`La cantidad no puede superar las acciones disponibles (${available})`);
      return;
    }

    setIsLoading(true);
    setSubmitError(null);
    try {
      const requestBody = {
        quantity: formValues.quantity!,
        partner_id: formValues.partner_id,
        protector_id: formValues.protector_id,
        minute_date: formValues.minute_date,
        minute_number: formValues.minute_number,
      };

      const response = await createProtectorShareReceipt(requestBody);
      
      if (response.success && 'data' in response) {
        toast.success('Cesión de acciones creada exitosamente');
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMsg = 'message' in response ? response.message : 'Error al crear la cesión de acciones';
        setSubmitError(errorMsg);
      }
    } catch (error: any) {
      console.error('Error creating share receipt:', error);
      const errorMsg = error?.message || 'Error al crear la cesión de acciones';
      setSubmitError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Dropdown helpers for Partner
  const partnerItems = metadata?.partners?.map((partner) => ({
    label: partner.name,
    disabled: false,
  })) || [];

  const getSelectedPartnerName = () => {
    const selectedPartner = metadata?.partners?.find(
      (partner) => partner.id === formValues.partner_id
    );
    return selectedPartner ? selectedPartner.name : 'Seleccionar';
  };

  const handlePartnerChange = (selectedLabel: string) => {
    const partner = metadata?.partners?.find((p) => p.name === selectedLabel);
    if (partner) {
      updateFormValues({ partner_id: partner.id });
    }
  };

  const selectedPartner = metadata?.partners?.find(
    (partner) => partner.id === formValues.partner_id
  );
  const availableShares = selectedPartner?.available_share_quantity;
  const cededShares = selectedPartner?.ceded_share_quantity;

  // Dropdown helpers for Protector
  const protectorItems = metadata?.protectors?.map((protector) => ({
    label: protector.name,
    disabled: false,
  })) || [];

  const getSelectedProtectorName = () => {
    const selectedProtector = metadata?.protectors?.find(
      (protector) => protector.id === formValues.protector_id
    );
    return selectedProtector ? selectedProtector.name : 'Seleccionar';
  };

  const handleProtectorChange = (selectedLabel: string) => {
    const protector = metadata?.protectors?.find((p) => p.name === selectedLabel);
    if (protector) {
      updateFormValues({ protector_id: protector.id });
    }
  };

  const isProtectorDisabled = !!protectorId;

  const isFormIncomplete = !formValues.partner_id || 
                           !formValues.protector_id || 
                           !formValues.minute_date || 
                           !formValues.minute_number || 
                           !formValues.quantity ||
                           (availableShares != null && formValues.quantity > availableShares);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nueva cesión de acciones"
      width='700px'
      primaryText='Confirmar creación'
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isLoading || isLoadingMetadata || isFormIncomplete}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      {isLoadingMetadata ? (
        <Card display='flex' justifyContent='center' alignItems='center' height='200px'>
          <Spinner />
        </Card>
      ) : (
        <Card margin={{ top: '4' }}>
            
            {/* Protector */}
            <Input
              label="Protector *"
              value={getSelectedProtectorName()}
              readOnly
              disabled
              size="md"
            />

            {/* Socio */}
            <DropDown
              id="partner"
              label="Socio (cedente, SOCIO B) *"
              title={getSelectedPartnerName()}
              items={partnerItems}
              onChange={handlePartnerChange}
              size="md"
              marginTop="4px"
            />

            {selectedPartner && (
              <Card
                margin={{ top: '2' }}
                style={{
                  border: '1px dashed #2a9d8f',
                  padding: '12px',
                  borderRadius: '4px',
                }}
              >
                <P withMargin={false} variant='neutral' type='3' modifier='strong'>
                  Disponibles para ceder:{' '}
                  {availableShares ?? 0} acciones
                  {cededShares != null && cededShares > 0
                    ? ` (cedidas históricas: ${cededShares})`
                    : ''}
                </P>
              </Card>
            )}

            {/* Fecha del acta */}
            <Card margin={{ top: '2' }}>
              <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                Fecha del acta *
              </P>
              <Card margin={{ top: '2' }}>
                <DatePicker
                  defaultValue={formValues.minute_date}
                  onChange={(date: string) => updateFormValues({ minute_date: date })}
                  disabled={isLoading}
                  displayFormat="DD/MM/AA"
                />
              </Card>
            </Card>

            {/* Número de acta */}
            <Input
              label="Número de acta *"
              value={formValues.minute_number}
              onChange={(e) => updateFormValues({ minute_number: e.target.value })}
              disabled={isLoading}
              size="md"
            />

            {/* Cantidad */}
            <Input
              label="Cantidad *"
              type="number"
              value={formValues.quantity?.toString() || ''}
              onChange={(e) => updateFormValues({ quantity: e.target.value ? parseInt(e.target.value) : null })}
              disabled={isLoading}
              size="md"
            />
            
            {submitError && (
              <Card margin={{ top: '1' }}>
                <P variant='danger' type='3' withMargin={false}>
                  {submitError}
                </P>
              </Card>
            )}
        </Card>
      )}
    </Modal>
  );
};

export default NewProtectorShareReceiptModal;
